import { Icon } from '@buggy/shared'
import { Button, Flex, Spin, Table, Typography, type GetProp } from 'antd'
import { isEmpty } from 'lodash-es'
import { FunctionComponent, useState, type ReactNode } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import styled, { useTheme } from 'styled-components'
import DeleteButton from '@/components/common/buttons/delete-button'
import CollaborativeItemList from '@/components/common/collaborative/collaborative-item-list'
import PermissionInsufficientTooltip from '@/components/common/permission-insufficient-tooltip'
import { NoData } from '@/components/kol/detail/no-data'
import { AvatarImage } from '@/components/workspace/avatar-image'
import useFormatPrice from '@/hooks/use-format-price'
import { useDirectoryActionPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  quotationSchema,
  type Quotation,
} from '@/types/schema/directory/quotation'

type ColumnProps = GetProp<typeof Table<Quotation>, 'columns'>[number]

export interface QuotationListProps {
  data?: Quotation[]
  onDelete?: (quotation: Quotation) => Promise<void>
  onClickEdit?: (quotation: Quotation) => void
  hasNextPage?: boolean
  isLoadMore?: boolean
  onLoadMore?: VoidFunction
}

const QuotationList: FunctionComponent<QuotationListProps> = ({
  data,
  onDelete,
  onClickEdit,
  hasNextPage = false,
  isLoadMore = false,
  onLoadMore = (): void => {},
}) => {
  const { formatMessage, formatDate } = useIntl()
  const { formatPrice } = useFormatPrice()
  const theme = useTheme()

  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([])
  const [hoveredRowKey, setHoveredRowKey] = useState<number | null>(null)

  const directoryActionPermission = useDirectoryActionPermission()

  const [sentryRef] = useInfiniteScroll({
    loading: isLoadMore,
    hasNextPage,
    onLoadMore,
    disabled: !hasNextPage,
  })

  if (isEmpty(data)) {
    return <NoData />
  }

  const columns: ColumnProps[] = [
    {
      title: formatMessage({
        id: 'general:column_title_project_name',
      }),
      dataIndex: quotationSchema.keyof().enum.name,
      render: (_, quotation): ReactNode => {
        const isLegacyQuotation = isEmpty(quotation.item_list)

        return (
          <Flex
            align='center'
            gap={12}
            style={{ paddingLeft: isLegacyQuotation ? 0 : 8 }}
          >
            {!isLegacyQuotation && (
              <ExpandButton
                icon={
                  <Icon
                    fontSize={14}
                    name={
                      expandedRowKeys.includes(quotation.id)
                        ? 'chevron-down'
                        : 'chevron-right'
                    }
                  />
                }
                type='text'
              />
            )}
            <Flex
              gap={4}
              style={{
                flexDirection: isLegacyQuotation ? 'column' : 'column-reverse',
              }}
            >
              <StartDate>{formatDate(quotation.start_date)}</StartDate>
              <QuotationName ellipsis={{ tooltip: quotation.name, rows: 2 }}>
                {quotation.name}
              </QuotationName>
            </Flex>
          </Flex>
        )
      },
    },
    {
      title: formatMessage({
        id: 'general:column_title_collaboration_compensation',
      }),
      width: 100,
      dataIndex: quotationSchema.keyof().enum.total_budget,
      render: (value: Quotation['total_budget']) => formatPrice(value),
    },
    {
      title: formatMessage({
        id: 'general:column_title_quotation_issuer',
      }),
      width: 200,
      dataIndex: quotationSchema.keyof().enum.manager_id,
      render: (_, quotation): ReactNode => {
        const isLegacyQuotation = isEmpty(quotation.item_list)

        return (
          <Flex justify='space-between'>
            {!isEmpty(quotation.manager_name) && (
              <Flex align='center' gap={3}>
                <AvatarImage name={quotation.manager_name} size='small' />
                <ManagerName>{quotation.manager_name}</ManagerName>
              </Flex>
            )}
            {(onDelete ?? onClickEdit) && hoveredRowKey === quotation.id && (
              <Flex>
                {onClickEdit && !isLegacyQuotation && (
                  <PermissionInsufficientTooltip
                    insufficient={!directoryActionPermission}
                  >
                    <Button
                      icon={
                        <Icon
                          color={theme.colors.brand.primary}
                          fontSize={16}
                          name='edit-3'
                        />
                      }
                      size='small'
                      type='text'
                      onClick={(event): void => {
                        event.stopPropagation()
                        onClickEdit(quotation)
                      }}
                    />
                  </PermissionInsufficientTooltip>
                )}
                {onDelete && (
                  <PermissionInsufficientTooltip
                    insufficient={!directoryActionPermission}
                  >
                    <DeleteButton
                      confirmModalContent={formatMessage({
                        id: 'irm:delete_quotation_modal_content',
                      })}
                      confirmModalTitle={formatMessage({
                        id: 'kol:edit_coop_field_delete_price',
                      })}
                      disabled={!directoryActionPermission}
                      iconColor={theme.colors.brand.primary}
                      iconSize={16}
                      size='small'
                      type='text'
                      onDelete={async (): Promise<void> =>
                        await onDelete(quotation)
                      }
                    />
                  </PermissionInsufficientTooltip>
                )}
              </Flex>
            )}
          </Flex>
        )
      },
    },
  ]

  return (
    <StyledTable
      columns={columns}
      dataSource={data}
      expandable={{
        expandedRowRender: (quotation) => (
          <ExpandedRow>
            <p>
              {formatMessage({ id: 'general:column_title_quotation_items' })}
            </p>
            <CollaborativeItemList items={quotation.item_list} />
          </ExpandedRow>
        ),
        onExpandedRowsChange: (expandedRowKeys: number[]) => {
          setExpandedRowKeys(expandedRowKeys)
        },
        expandRowByClick: true,
        rowExpandable: (quotation) => !isEmpty(quotation.item_list),
      }}
      footer={() =>
        hasNextPage && (
          <Flex align='center' justify='center' ref={sentryRef}>
            <Spin />
          </Flex>
        )
      }
      pagination={false}
      rowKey={(quotation) => quotation.id}
      onRow={(quotation) => ({
        onMouseEnter: (): void => {
          setHoveredRowKey(quotation.id)
        },
        onMouseLeave: (): void => {
          setHoveredRowKey(null)
        },
      })}
    />
  )
}

const ExpandButton = styled(Button)`
  &.ant-btn {
    --ant-control-height: 14px;
    border: none;
    line-height: 14px;
  }

  &.ant-btn.ant-btn-icon-only:not(.ant-btn-sm):not(.ant-btn-block) {
    padding: 0;
  }
`

const QuotationName = styled(Typography.Paragraph)`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;

  &.ant-typography {
    margin: 0;
  }
`

const ManagerName = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const StartDate = styled.div`
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const StyledTable = styled(Table<Quotation>)`
  & .ant-table {
    --ant-table-expand-icon-size: 0;
    --ant-table-footer-bg: transparent;
  }

  &.ant-table-wrapper {
    table {
      border-collapse: collapse;
    }

    .ant-table-tbody > tr > td {
      border: none;
    }
  }

  .ant-table-content {
    .ant-table-expand-icon-col {
      width: 0;
      border: none;
      padding: 0;
    }
  }

  .ant-table-thead {
    .ant-table-row-expand-icon-cell {
      padding: 0;
    }

    .ant-table-cell {
      font-weight: 400;

      &:nth-child(2) {
        padding: 16px 8px;
      }
    }
  }

  &.ant-table-wrapper tr.ant-table-expanded-row > td {
    padding: 0;
    background: ${({ theme }): string => theme.colors.background.white};
  }

  .ant-table-tbody {
    .ant-table-row:has(
        + .ant-table-expanded-row:not([style*='display: none'])
      ) {
      border-bottom: none;
    }

    .ant-table-row,
    .ant-table-expanded-row {
      border-bottom: var(--ant-line-width) var(--ant-line-type)
        var(--ant-table-border-color);
    }

    .ant-table-row {
      .ant-table-row-expand-icon-cell {
        padding: 0;
      }

      .ant-table-expanded-row {
        .ant-table-cell {
          padding: 0;
        }
      }

      .ant-table-cell:nth-child(2) {
        padding: 16px 8px 16px 0;
      }
    }
  }
`

const ExpandedRow = styled.div`
  margin: 0 8px 16px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  background: ${({ theme }): string => theme.colors.background.grey};

  p {
    color: ${({ theme }): string => theme.colors.text.primary};
    font-size: 15px;
    font-weight: 700;
    line-height: 22px;
    margin: 0;
  }
`

export default QuotationList
